import * as moment from 'moment-timezone';
import {useEffect, useMemo, useState} from "react";
import 'react-big-calendar/lib/css/react-big-calendar.css';

import {Calendar, momentLocalizer, Views} from 'react-big-calendar';
import {Box, LinearProgress} from "@mui/material";
import {blue, grey} from '@mui/material/colors';
import {useLazyQuery, useMutation} from "@apollo/client";

import {useTimezone} from '../hooks/useTimezone';
import {useCurrentUser} from "../hooks/useCurrentUser";
import {useShiftUpdateCount} from "../hooks/useShiftUpdateCount";
import useBigCalendar from "../hooks/useBigCalendar";
import useFirstDayOfTheWeek, {DEFAULT_START_DAY} from "../hooks/useFirstDayOfTheWeek";
import "../css/App.css";

import {
    GET_FILTERED_SHIFT_PLANS,
    ENROLL_PERFORMER_FROM_SHIFT_PLANS,
    UNENROLL_PERFORMER_FROM_SHIFT_PLANS,
    MY_SHIFTS
} from "../queries/GraphQLQueries";
import CustomAlert from "../components/CustomAlert";
import MyAvailabilityViewHeader from '../components/my-availability/MyAvailabilityViewHeader';
import ServiceTypeTitle from '../components/common/ServiceTypeTitle';
import {
    TWENTY_FOUR_HOUR_TIME_FORMAT,
    DATE_WITH_TIME_FORMAT
} from '../config/DateTimeConstants';
import {ApolloFetchPolicies} from "../constants/FetchPolicies";
import {StorageKeys} from '../constants/StorageKeys';
import {
    buildShiftPlanSelections,
    constructShiftPlansAndShiftArrayForWeekView,
    fetchOverlappingServiceTypes,
    getOverlappingShiftPlanIds,
    isWithinEnrollmentRange,
    splitIntoHourlyChunks
} from "../helpers/ShiftPlanHelper";
import {EVENT_TYPE, SHIFT_PLAN_ERROR_MESSAGE, SHIFT_PLAN_SUCCESS_MESSAGE} from '../helpers/ShiftPlanHelper';
import {retrieveServiceTypeIcon} from '../helpers/ServiceTypeUtils';

const UPDATED_SHIFTS_MARKER = '*';
const AVAILABLE_LABEL_TEXT = 'Available';
const SCHEDULED_LABEL_TEXT = 'Scheduled';
const SHIFT_PLAN_DATE_BUFFER_IN_DAYS = 1

export default function MyAvailability() {

    const {timezone} = useTimezone();
    const {currentUser: currentPerformer} = useCurrentUser();
    const {fetchDataUpdates, keystore, resetDataUpdateCount} = useShiftUpdateCount();
    useFirstDayOfTheWeek(DEFAULT_START_DAY);

    const [alertMessage, setAlertMessage] = useState(null);
    const [shiftPlans, setShiftPlans] = useState();
    const [shifts, setShifts] = useState();
    const [currentView, setCurrentView] = useState("month");
    const [currentDate, setCurrentDate] = useState(moment().toDate());

    const {calendarHeight} = useBigCalendar(currentView);

    const refetchDetailedShiftPlans = {refetchQueries: ['GetShiftPlansFilteredByPerformer', 'GetMyShifts']};
    const [fetchFilteredShiftPlans, {loading, error, data}] = useLazyQuery(GET_FILTERED_SHIFT_PLANS, {fetchPolicy: 'network-only'});
    const [fetchFilteredShifts, {loading: loadingMyShifts, error: shiftErrors, data: myShiftsData}] = useLazyQuery(MY_SHIFTS, {fetchPolicy: 'network-only'});
    const [enrollPerformer, {loading: enrollingPerformer}] = useMutation(ENROLL_PERFORMER_FROM_SHIFT_PLANS, refetchDetailedShiftPlans);
    const [unenrollPerformer, {loading: unenrollingPerformer}] = useMutation(UNENROLL_PERFORMER_FROM_SHIFT_PLANS, refetchDetailedShiftPlans);

    const isLoading = enrollingPerformer || unenrollingPerformer || loading;
    const currentDateInUTC = moment().tz(timezone).toDate();
    const DARK_GOLD_COLOR = '#CC9933';
    const DARK_BLUE = blue[700];
    const lastShiftUpdateCheckedAt = moment(keystore.getKey(StorageKeys.LAST_SHIFT_UPDATE_CHECK_TIME), DATE_WITH_TIME_FORMAT);

    useEffect(() => {
        fetchDataUpdates(ApolloFetchPolicies.NETWORK_ONLY);
    }, []);

    useEffect(() => {

        if(!currentPerformer) {
            return;
        }

        updateFilteredShiftPlans();
    }, [currentDate, currentView, currentPerformer]);

    useEffect(() => {

        if (error) {
            handleError(error);
            return;
        }

        if (!data || !data.shiftPlans) {
            return;
        }

        const mappedShiftPlans = data.shiftPlans.map(shiftPlan => ({...shiftPlan, eventType: EVENT_TYPE.SHIFT_PLAN}));
        const shiftPlanEvents = mappedShiftPlans.flatMap(shiftPlan => populateEventObject(shiftPlan, shiftPlan.startTime, shiftPlan.endTime));

        setShiftPlans(shiftPlanEvents);

    }, [data, timezone, resetDataUpdateCount]);

    useEffect(() => {

        if (!myShiftsData || !myShiftsData.me) {
            return;
        }

        const myShifts = myShiftsData.me.availability.map(shift => ({ ...shift, eventType: EVENT_TYPE.SHIFT }));
        const shiftEvents = myShifts
            .flatMap(shift => populateEventObject(shift, shift.performingFrom, shift.to))
            .filter(shift => !shift.isDeleted || (shift.isDeleted && moment(shift.updated).isAfter(lastShiftUpdateCheckedAt)));

        setShifts(shiftEvents);

    }, [myShiftsData, timezone, resetDataUpdateCount]);

    const populateEventObject = (event, startTime, endTime) => {
        const build = (start, end) => {
            const matchingEnrollment = event.enrollments?.find(
                enrollment => enrollment.user.id === currentPerformer.id
            );

            const isEnrolled = matchingEnrollment
                ? isWithinEnrollmentRange(start, end, matchingEnrollment.fromTime, matchingEnrollment.toTime)
                : false;

            const isShiftGenerated =
                matchingEnrollment && matchingEnrollment.availability
                    ? isWithinEnrollmentRange(start, end, matchingEnrollment.availability.performingFrom, matchingEnrollment.availability.to)
                    : false;

            return {
                id: event.id,
                start: adjustTimeAsPerTimeZone(start.toDate()),
                end: adjustTimeAsPerTimeZone(end.toDate()),
                updated: event.updated,
                enrollments: event.enrollments,
                serviceType: event.bookableServiceTypePlans?.map(plan => plan.serviceType)
                    || event.bookableServiceTypes?.map(service => service.serviceType),
                eventType: event.eventType,
                isDeleted: event.isDeleted || false,
                isEnrolled: isEnrolled,
                isShiftGenerated: isShiftGenerated
            };
        };

        if (currentView === Views.WEEK) {
            const timeChunks = splitIntoHourlyChunks(startTime, endTime, timezone);
            return timeChunks.map(([chunkStart, chunkEnd]) => build(chunkStart, chunkEnd));
        }

        return [build(moment(startTime), moment(endTime))];
    };


    const buildEventTitle = (event, startTime, endTime, isEnrolled) => {
        const formattedStartTime = moment(startTime).tz(timezone).format(TWENTY_FOUR_HOUR_TIME_FORMAT);
        const formattedEndTime = moment(endTime).tz(timezone).format(TWENTY_FOUR_HOUR_TIME_FORMAT);

        const serviceTypes = event.serviceType;

        const serviceTypeIcons = retrieveServiceTypeIcon(serviceTypes?.map(type => type.keycode));
        const serviceTypeDescriptions = serviceTypes?.map(type => type.description);
        let eventTitle = `${formattedStartTime} - ${formattedEndTime}`;

        if (moment(event.updated).isAfter(lastShiftUpdateCheckedAt) && event.eventType === EVENT_TYPE.SHIFT) {
            eventTitle = `${eventTitle} ${UPDATED_SHIFTS_MARKER}`;
        }

        let label;

        if (currentView === Views.WEEK) {
            eventTitle = null;

            if (event.eventType === EVENT_TYPE.SHIFT_PLAN && isEnrolled) {
                label = AVAILABLE_LABEL_TEXT;
            } else if (event.eventType === EVENT_TYPE.SHIFT && !event.isDeleted) {
                label = SCHEDULED_LABEL_TEXT;
            }
        }

        return <ServiceTypeTitle label={label} title={eventTitle} serviceTypeIcons={serviceTypeIcons} serviceTypeDescriptions={serviceTypeDescriptions} />;
    };

    const updateFilteredShiftPlans = () => {
        fetchFilteredShifts({
            variables: {
                startTime: moment(currentDate).tz(timezone).startOf(currentView).toISOString(),
                endTime: moment(currentDate).tz(timezone).endOf(currentView).toISOString(),
                includeDeletedShifts: true,
                includeOnlyAccreditedServiceTypes: true
            }
        });

        setShiftPlans([]);

        if (currentView === Views.WEEK) {
            fetchFilteredShiftPlans({
                variables: {
                    startTime: moment(currentDate).tz(timezone).startOf(currentView).subtract(SHIFT_PLAN_DATE_BUFFER_IN_DAYS, 'day').toISOString(),
                    endTime: moment(currentDate).tz(timezone).endOf(currentView).toISOString(),
                    performerId: currentPerformer.id
                }
            });
        }
    }

    const adjustTimeAsPerTimeZone = (time) => {
        return moment(time).tz(timezone).toDate();
    }

    const handleCloseSnackbar = () => setAlertMessage(null);

    const localizer = momentLocalizer(moment);

    const sessionStyle = (event) => {
        if (event.eventType === EVENT_TYPE.ALL_DAY) {
            return {
                className: EVENT_TYPE.ALL_DAY,
                style: {
                    backgroundColor: grey[200],
                    border: 'none',
                    color: 'transparent',
                    pointerEvents: 'auto'
                }
            };
        }

        let customBorderStyle = `0.07rem solid ${DARK_BLUE}`;
        let customBackgroundColor = blue[50];
        let textColor = 'black';
        let customFontWeight = 'normal';

        const isShiftUpdatedSinceUserLastChecked = moment(event.updated).isAfter(lastShiftUpdateCheckedAt);

        if ((event.isShiftGenerated || event.eventType === EVENT_TYPE.SHIFT) && !event.isDeleted) {
            customBackgroundColor = DARK_GOLD_COLOR;
            textColor = 'white';
            customBorderStyle = `0.07rem solid white`;
        }

        if (event.isEnrolled) {
            customBackgroundColor = DARK_BLUE;
            textColor = 'white';
            customBorderStyle = `0.07rem solid white`;
        }

        if (isShiftUpdatedSinceUserLastChecked && event.eventType === EVENT_TYPE.SHIFT) {
            customBorderStyle = '0.15rem solid black';
            customFontWeight = 'bold';
        }

        let timeslotBackgroundStyle = {
            backgroundColor: customBackgroundColor,
            border: customBorderStyle,
            color: textColor,
            fontWeight: customFontWeight,
            textDecoration: (event.isDeleted && isShiftUpdatedSinceUserLastChecked && event.eventType === EVENT_TYPE.SHIFT) ? 'line-through' : 'none'
        };

        return {className: `${event.id}-isShift-${event.eventType === EVENT_TYPE.SHIFT}-isDeleted-${event.isDeleted}`, style: timeslotBackgroundStyle};
    };

    const handleCalendarNavigate = (date, view) => {
        setCurrentView(view);
        setCurrentDate(date);
    };

    const handleViewChange = (view) => {
        setCurrentView(view);
    };

    const handleEnrollmentAction = (shiftPlansToAction, mutationFunction, successMessage) => {

        if (shiftPlansToAction.length === 0) {
            setAlertMessage({ children: SHIFT_PLAN_ERROR_MESSAGE.SHIFT_IS_IN_PAST, severity: 'error' });
            return;
        }

        const shiftPlanIds = Array.from(new Set(
            shiftPlansToAction.map(shiftPlan => shiftPlan.id)
        ));

        mutationFunction({
            variables: {shiftPlanIds, performerId: currentPerformer.id}
        }).then(() => setAlertMessage({ children: successMessage, severity: 'success' })).catch(handleError);
    };

    const handleEnrollAll = () => {
        if (!shiftPlans || shiftPlans.length < 1) {
            return;
        }

        const shiftPlansToEnroll = shiftPlans.filter(shiftPlan => moment(shiftPlan.end).isAfter(currentDateInUTC));
        handleEnrollmentAction(shiftPlansToEnroll, enrollPerformer, SHIFT_PLAN_SUCCESS_MESSAGE.ENROLLED_IN_ALL_SHIFTS);
    };

    const handleUnenrollAll = () => {
        if (!shiftPlans || shiftPlans.length < 1) {
            return;
        }

        const shiftPlansToUnenroll = shiftPlans
            .filter(shiftPlan => moment(shiftPlan.end).isAfter(currentDateInUTC))
            .filter(shiftPlan => shiftPlan.isEnrolled && !shiftPlan.isShiftGenerated);

        if (!shiftPlansToUnenroll || shiftPlansToUnenroll.length < 1) {
            return;
        }

        handleEnrollmentAction(shiftPlansToUnenroll, unenrollPerformer, SHIFT_PLAN_SUCCESS_MESSAGE.UNENROLLED_FROM_ALL_SHIFTS);
    };

    const handleSelectShiftPlan = (event) => {
        if (currentView !== Views.WEEK) {
            return;
        }

        const currentPerformerId = currentPerformer.id;
        const isEnrolled = event.isEnrolled;
        const isShiftDeleted = event.isDeleted || false;

        if (isShiftDeleted) {
            setAlertMessage({ children: SHIFT_PLAN_ERROR_MESSAGE.SHIFT_IS_DELETED, severity: 'error' });
            return;
        }

        if (moment(event.end).isBefore(currentDateInUTC)) {
            setAlertMessage({ children: SHIFT_PLAN_ERROR_MESSAGE.SHIFT_IS_IN_PAST, severity: 'error' });
            return;
        }

        if (event.eventType === EVENT_TYPE.SHIFT) {
            setAlertMessage({ children: SHIFT_PLAN_ERROR_MESSAGE.SHIFT_ALREADY_GENERATED, severity: 'error' });
            return;
        }

        let variables;
        if (event.eventType === EVENT_TYPE.ALL_DAY) {
            if (!event.shiftPlanIds || event.shiftPlanIds.length === 0) {
                setAlertMessage({ children: SHIFT_PLAN_ERROR_MESSAGE.NO_SHIFT_PLANS_AVAILABLE, severity: 'error' });
                return;
            }

            variables = {
                performerId: currentPerformerId,
                shiftPlanSelections: buildShiftPlanSelections(event, shiftPlans)
            };
        } else {
            variables = {
                performerId: currentPerformerId,
                shiftPlanSelections: (event.shiftPlanIds ?? [event.id]).map(shiftPlanId => ({ shiftPlanId, fromTime: event.start, toTime: event.end }))
            };
        }

        const updateEnrollmentMutation = isEnrolled ? unenrollPerformer : enrollPerformer;

        updateEnrollmentMutation({ variables }).catch(handleError);
    };

    const handleError = (error) => {
        setAlertMessage({children: error?.message, severity: 'error'});
        console.log('error ' + error);
    };

    const formats = useMemo(() => ({
        eventTimeRangeFormat: () => "",
    }), []);

    const buildEventsForBulkEnrollment = (shiftPlans) => {

        const startOfWeek = moment(currentDate).startOf('week');

        return Array.from({ length: 7 }).map((_, i) => {
            const day = startOfWeek.clone().add(i, 'days');
            const dayStart = day.clone().startOf('day');
            const dayEnd = day.clone().endOf('day');

            const overlappingShiftPlanIds = getOverlappingShiftPlanIds(shiftPlans, dayStart, dayEnd);

            return {
                start: dayStart.toDate(),
                end: dayEnd.toDate(),
                allDay: true,
                eventType: EVENT_TYPE.ALL_DAY,
                shiftPlanIds: overlappingShiftPlanIds
            };
        });
    };

    const events = () => {
        let result;
        if (currentView === Views.WEEK) {
            result =  [...buildEventsForBulkEnrollment(shiftPlans), ...constructShiftPlansAndShiftArrayForWeekView(shiftPlans, shifts)];
        } else {
            result = shifts ? [...shifts] : [];
        }

        return result.map(event => ({
            ...event,
            ...(event.eventType !== EVENT_TYPE.ALL_DAY && { title: buildEventTitle(event, event.start, event.end, event.isEnrolled) })
        }));
    };

    const calendarComponents = {toolbar: ({onNavigate, onView}) =>
                                                <MyAvailabilityViewHeader
                                                    currentDate={currentDate}
                                                    currentView={currentView}
                                                    onRefresh={updateFilteredShiftPlans}
                                                    onEnrollAll={handleEnrollAll}
                                                    onUnenrollAll={handleUnenrollAll}
                                                    onNavigate={onNavigate}
                                                    onView={onView}
                                                    isLoading={isLoading} />
    };

    return <>
        <Box className="my-availability-calendar">
            {isLoading && <LinearProgress/>}
            {!isLoading && <Box sx={{my: 0.5}}/>}
            <Calendar
                localizer={localizer}
                popup={true}
                formats={formats}
                defaultView={Views.MONTH}
                events={events()}
                style={calendarHeight}
                onSelectEvent={handleSelectShiftPlan}
                eventPropGetter={sessionStyle}
                onNavigate={handleCalendarNavigate}
                onView={handleViewChange}
                components={calendarComponents}
                tooltipAccessor={null}
                views={[Views.WEEK, Views.MONTH]}
                endAccessor={({ end }) => new Date(end.getTime() - 1)}
            />
        </Box>
        <CustomAlert alertMessage={alertMessage} handleCloseCustomAlert={handleCloseSnackbar}/>
    </>
};